import { useFormContext } from 'react-hook-form'
import { FormField } from '@/components/serge/FormField'
import { type LegalEntityFormData } from '@/lib/legalEntitySchema'

export function RGPDStep() {
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<LegalEntityFormData>()

  const processingPurposes = watch('processingPurposes') || []
  const legalBases = watch('legalBases') || []
  const subProcessors = watch('subProcessors') || []

  return (
    <div className="space-y-6">
      <div className="border border-border bg-surface p-5">
        <h3 className="text-lg font-semibold text-foreground mb-1">Conformité RGPD</h3>
        <p className="text-sm text-foreground-secondary mb-6">
          Informations pour la politique de confidentialité
        </p>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              label="Responsable de traitement"
              error={errors.dataControllerName}
              tooltip="Personne physique ou morale qui détermine les finalités et moyens du traitement"
            >
              <input
                {...register('dataControllerName')}
                className="w-full border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition"
                placeholder="Nom de l'entité"
              />
            </FormField>

            <FormField
              label="Contact DPO"
              error={errors.dpoEmail}
              tooltip="Email du Délégué à la Protection des Données (si désigné)"
            >
              <input
                type="email"
                {...register('dpoEmail')}
                className="w-full border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition"
                placeholder="dpo@exemple.fr"
              />
            </FormField>

            <FormField
              label="Email privacy"
              error={errors.privacyContactEmail}
              tooltip="Email de contact pour les questions relatives à la vie privée"
            >
              <input
                type="email"
                {...register('privacyContactEmail')}
                className="w-full border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition"
                placeholder="privacy@exemple.fr"
              />
            </FormField>
          </div>

          <FormField
            label="Finalités du traitement"
            hint="Une finalité par ligne (ex: Gestion des commandes, Support client)"
            tooltip="Objectifs poursuivis par la collecte et le traitement des données personnelles"
          >
            <textarea
              value={(processingPurposes ?? []).join('\n')}
              onChange={(e) => {
                const values = e.target.value.split('\n').map((line) => line.trim())
                setValue('processingPurposes', values, { shouldDirty: true })
              }}
              className="w-full min-h-[120px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
              placeholder="Gestion des comptes clients&#10;Traitement des commandes&#10;Support technique"
            />
          </FormField>

          <FormField
            label="Bases légales"
            hint="Une base par ligne (ex: Consentement, Exécution du contrat, Intérêt légitime)"
            tooltip="Fondement juridique autorisant le traitement des données"
          >
            <textarea
              value={(legalBases ?? []).join('\n')}
              onChange={(e) => {
                const values = e.target.value.split('\n').map((line) => line.trim())
                setValue('legalBases', values, { shouldDirty: true })
              }}
              className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
              placeholder="Consentement de la personne concernée&#10;Exécution d'un contrat&#10;Obligation légale"
            />
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Politique de conservation"
              error={errors.retentionPolicy}
              tooltip="Durée pendant laquelle les données sont conservées"
            >
              <textarea
                {...register('retentionPolicy')}
                className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                placeholder="Ex: 3 ans après la dernière activité"
              />
            </FormField>

            <FormField
              label="Mesures de sécurité"
              error={errors.securityMeasures}
              tooltip="Mesures techniques et organisationnelles mises en place"
            >
              <textarea
                {...register('securityMeasures')}
                className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
                placeholder="Chiffrement des données, accès restreint, sauvegardes régulières..."
              />
            </FormField>
          </div>

          <FormField
            label="Sous-traitants"
            hint="Un sous-traitant par ligne (ex: Stripe pour les paiements)"
            tooltip="Prestataires qui traitent des données pour votre compte"
          >
            <textarea
              value={(subProcessors ?? []).join('\n')}
              onChange={(e) => {
                const values = e.target.value.split('\n').map((line) => line.trim())
                setValue('subProcessors', values, { shouldDirty: true })
              }}
              className="w-full min-h-[100px] border border-border bg-bg p-3 text-sm text-foreground focus:outline-none focus:border-primary transition resize-y"
              placeholder="Stripe (paiements)&#10;SendGrid (emails)&#10;AWS (hébergement)"
            />
          </FormField>

          <div className="border border-border bg-bg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                {...register('transferOutsideEu')}
                className="w-4 h-4 border-border text-primary focus:ring-primary"
              />
              <div>
                <div className="text-sm font-medium text-foreground">
                  Transfert de données hors UE
                </div>
                <div className="text-xs text-foreground-muted mt-0.5">
                  Cochez si des données sont transférées vers des pays hors Union Européenne
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )
}
